import {GetServerSideProps, InferGetServerSidePropsType} from "next";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {ReactElement} from "react";
import Layout from "../../components/common/Layout";
import VerkaufDetailedTitle from "../../components/VerkaufDetailedTitle/VerkaufDetailedTitle";
import VermietungsCarousel from "../../components/VermietungsCarousel/VermietungsCarousel";
import {NoImageAvailablePlaceholder,} from "../../data/StaticImages";
import {useRouter} from "next/router";
import {ImmoImageTypes, ServerSideImageFetch} from "../../helpers/ImageHelpers";
import {getObjectInServerSideProps} from "../../data/ReactQueries";
import {calcPersonSleepingBathrooms} from "../../components/CalculationHelper/CalcHelpers";
import ObjectDetails from "../../components/VermietungsDetailedTabGroup/ObjectDetails";
import VerkaufsAnfrageForm from "../../components/VerkaufsAnfrageForm/VerkaufsAnfrageForm";
import {ObjectInterfaceWithCity} from "../../interfaces/ObjectInterface";
import {getSafeLocale} from "../../helpers/LocalizeObject";
import {EXTERNAL_DATA_URL} from "../sitemap.xml";
import Head from "next/head";
import {useTranslation} from "next-i18next";
import {useQuery} from '@tanstack/react-query';
import HomeBanner from "../../components/common/HomeBanner"
import HomeMarinaAmMittelmeer from "../../components/common/HomeMarinaAmMittelmeer";
import HomeKaufenUndVerkaufen from "../../components/common/HomeKaufenUndVerkaufen";
import LastMinutes, {OfferInterface} from "../../components/common/LastMinutes";
import ImmobilieKaufenHome, {ImmobilieKaufenHomePropsInterface} from "../../components/common/ImmobilieKaufenHome";
import {Filter} from "../../data/Filter/Filter.config";
import {fetchItem} from "../../data/ReactQueries";

export default function Page<NextPageWithLayout>({
	                                                 objectData,
	                                                 serverSideImages,
	                                                 locale
                                                 }: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const fetchedObject: ObjectInterfaceWithCity = JSON.parse(objectData);
	const router = useRouter();
	const images = serverSideImages

	const personSleepingBathrooms = calcPersonSleepingBathrooms(fetchedObject);

	const {t} = useTranslation();
	const {
		data: objects,
		isLoading: objectsLoading,
		refetch
	} = useQuery(
		["lastMinutes", {[Filter.Rent.queryKey]: 1, locale: router.locale}],
		(): Promise<OfferInterface[]> => {
			return fetchItem(
				"last-minutes",
				{
					[Filter.Rent.queryKey]: 1
				},
				router.locale
			);
		}
	);

	const {
		data: SaleObjects,
	} = useQuery(
		[
			"SaleObjects",
			{[Filter.Sale.queryKey]: 1, locale: router.locale}
		],
		() => {
			return fetchItem(
				"objects",
				{
					[Filter.Sale.queryKey]: 1
				},
				router.locale
			);
		}
	);

	if (!fetchedObject) return <></>

	const jsonLd = {
		"@context": "http://schema.org",
		"@type": "RealEstateListing",
		brand: {
			"@type": "Brand",
			name: "LEON Inmobiliaria"
		},
		name: fetchedObject.headline,
		description: fetchedObject.description,
		url: `${EXTERNAL_DATA_URL}/${router.asPath}`,
		image: images[0]
	}

	const addressParts = [
		fetchedObject?.street,
		fetchedObject?.place,
		fetchedObject?.cityOfObject?.name,
		fetchedObject?.cityOfObject?.province?.name,
	];

	const address = addressParts.filter(Boolean).join(', ');

	const LatLng = fetchedObject?.gmaps || address;

	const mapUrl = LatLng ? `https://www.google.com/maps?q=${encodeURIComponent(LatLng)}&makers=${encodeURIComponent(LatLng)}&hl=${locale}&region=${locale}&output=embed` : null;
	const mapLink = LatLng ? `https://www.google.com/maps?q=${encodeURIComponent(LatLng)}&makers=${encodeURIComponent(LatLng)}&hl=${locale}&region=${locale}` : null;

	return (
		<div className="">
			<Head>
				<title>{fetchedObject.description}</title>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{__html: JSON.stringify(jsonLd, null, 2)}}
				/>
			</Head>
			<VermietungsCarousel images={images} object={fetchedObject}/>
			<VerkaufsAnfrageForm object={fetchedObject}/>
			<div className="lg:pt-[180px]"/>
			<div className="pb-[180px] xl:pb-14"/>
			<div className="w-full max-w-[90%] mx-auto">
				<div className="col-span-2">
					<VerkaufDetailedTitle
						furnishing={personSleepingBathrooms}
						objectId={fetchedObject.object_id}
						tln={(fetchedObject.tln && fetchedObject.tln + " / ")}
						title={fetchedObject.headline}
					/>
					<div className=" md:px-[70px]">
						<ObjectDetails object={fetchedObject}/>
					</div>
				</div>
				<div className="pb-1 mt-10">
					<div className="flex justify-center md:justify-between items-end mb-8">
						{mapLink && (
							<a href={mapLink} className="hidden md:flex">
								<img 
									src="/images/map-icon.svg"
									alt="Map Icon"
									title="Map Icon"
									className="w-8 h-8"
								/>
								<span className="text-[16px] underline text-black">{t("common.empuriabrava")}</span>
							</a>
						) }
						{mapLink && (
							<h2 className="font-light text-[40px] md:text-[48px]">
								{t("common.sitePlan")}
							</h2>
						)}
						<span className="w-[138px] hidden md:block"></span>
					</div>
					{mapUrl &&
						<div className="w-full h-96 rounded-[20px] shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)]">
							<iframe
								src={mapUrl}
								width="100%"
								height="100%"
								allowFullScreen
								loading="lazy"
								referrerPolicy="no-referrer-when-downgrade"
								className="rounded-[20px]"
							></iframe>
						</div>
					}
					<div className="mt-10" />
					<HomeBanner />
					<div className="mt-10" />
				</div>
			</div>

			<div className="w-full md:max-w-[90%] pl-4 md:pl-0 mx-auto">
				<LastMinutes objects={objects ?? []}/>
				<div className="mt-12" />
				<ImmobilieKaufenHome objects={SaleObjects?.items ?? []}/>
				<div className="mt-10" />
			</div>

			<HomeMarinaAmMittelmeer />
			
			<HomeKaufenUndVerkaufen />
		</div>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const {query} = context;
	const {id: objectId} = query;
	const safeLocale = getSafeLocale(context.locale)
	const object = await getObjectInServerSideProps(
		parseInt(objectId as string),
		safeLocale
	);

	const resp = await ServerSideImageFetch(
		parseInt(objectId as string),
		ImmoImageTypes.IMAGE
	);
	let tmpImages = {
		main: [NoImageAvailablePlaceholder],
		others: []
	};
	if (resp.ok) {
		tmpImages = await resp.json();
	}


	const images = [...tmpImages.main, ...tmpImages.others].map(
		(filename) => filename
	)

	return {
		props: {
			locale: safeLocale,
			serverSideImages: images,
			objectData: object ?? null,
			...(await serverSideTranslations(getSafeLocale(context.locale) as string, ["common"]))
			// object,
		}
	};
};

// export const getStaticProps: GetStaticProps = async (context) => {
//   return {
//     props: {
//       ...(await serverSideTranslations(getSafeLocale(context.locale) as string, ["common"])),
//       // object,
//     },
//   };
// };

// export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
//   return {
//     paths: [], //indicates that no page needs be created at build time
//     fallback: "blocking", //indicates the type of fallback
//   };
// };

Page.getLayout = function getLayout(page: ReactElement) {
	return (
		<Layout 
			headerNotTransparent={false}
			hasShadow={true}
		>
			{page}
		</Layout>
	);
};
